
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import { Box, Button, ButtonGroup, Chip, Grid, List, ListItemText, makeStyles, Tooltip, Typography, } from "@material-ui/core";
import { useCallback, useContext, useEffect, useState } from "react";
import { HelpOutline as HelpOutlineIcon } from "@material-ui/icons";
import AddIcon from "@material-ui/icons/Add";
import { BedSingle, Home, Pencil, Phone, PlusCircle } from "lucide-react";
import { observer } from "mobx-react-lite";
import useTranslation from "next-translate/useTranslation";
import Image from "next/image";
import { useRouter } from "next/router";
import { withAuthentication } from "../../components/Authentication";
import { PrimaryCard } from "../../components/Cards";
import Hidden from "../../components/HiddenSSRCompatible";
import { EmptyIllustration, RealEstateIconIllustration } from "../../components/Illustrations";
import { MobileButton } from "../../components/MobileMenuButton";
import Page from "../../components/Page";
import useNewPropertyDialog from "../../components/properties/NewPropertyDialog";
import useCreateRoomDialog from "../../components/rooms/dialogs/CreateRoomDialog";
import SearchFilter from "../../components/SearchFilter";
import { propertiesSteps } from "../../components/shepherdStep/guideStep";
import { FeatureGuard } from "../../components/subscriptions";
import useFillStore from "../../hooks/useFillStore";
import usePagination from "../../hooks/usePagination";
import { StoreContext } from "../../store";
import { getPresignedUrl } from "../../utils/fetch";
import { PermissionGate, PermissionGuard } from "../../utils/PermissionProvider";
const useStyles = makeStyles((theme) => ({
    avatarVacant: {
        backgroundColor: theme.palette.success.dark
    },
    avatarOccupied: {
        backgroundColor: theme.palette.text.disabled
    },
    chipOccupied: {
        color: theme.palette.info.contrastText,
        backgroundColor: theme.palette.text.disabled,
        borderRadius: 4
    },
    chipVacant: {
        color: theme.palette.info.contrastText,
        backgroundColor: theme.palette.success.dark,
        borderRadius: 4
    },
    ChipEdit: {
        borderRadius: 4,
        backgroundColor: ["#1f1f1f", "!important"],
        color: [theme.palette.info.contrastText, "!important"]
    },
    ChipAdd: {
        borderRadius: 4,
        backgroundColor: ["#FFD140", "!important"],
        color: "#1f1f1f"
    },
    ChipAddDisable: {
        borderRadius: 4,
        backgroundColor: ["#ddd", "!important"],
        color: "#1f1f1f"
    },
    CardHoverStyle: {
        height: "100%",
        padding: "8px 16px",
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            cursor: "pointer"
        }
    }
}));
function PropertyListItem({ property, cardIndex }) {
    const router = useRouter();
    const store = useContext(StoreContext);
    const { t } = useTranslation("common");
    const logo = property?.templateSetting?.logo?.selected;
    const logoUrl = property?.templateSetting?.logo?.url;
    const classes = useStyles();
    const [CreateRoomDialog, setOpenCreateRoomDialog] = useCreateRoomDialog();
    const limitRoom = store.subscription.selected?.usage.activeRoom ||
        store.subscription.selected?.features.maxRoom ||
        store.subscription.selected?.maxRoom;
    const handleCreateRoom = useCallback(() => {
        store.property.setSelected(property);
        setOpenCreateRoomDialog(true);
    }, [setOpenCreateRoomDialog, property, store.property]);
    const onClick = useCallback(async () => {
        store.property.setSelected(property);
        await router.push(`/properties/${property._id}/${encodeURI(t("Properties"))}/${encodeURIComponent(router.asPath)}`);
    }, [t, property, router, store.property]);
    return (<Box style={{ padding: "8px 16px" }}>
      <ListItemText primary={<Box style={{ padding: 10 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={12} lg={12}>
                <Box display="flex" alignItems="stretch">
                  {logo && logoUrl ? (<Image src={logoUrl} alt="Property Logo" width={100} height={45} style={{
                    objectFit: "contain",
                    maxWidth: 100,
                    maxHeight: 45,
                    width: "auto",
                    height: "auto",
                    display: "block"
                }} unoptimized/>) : (<RealEstateIconIllustration width={50} height={45}/>)}
                  <Typography variant="h5" style={{ marginLeft: 5, marginTop: 5 }}>
                    <Box fontWeight="fontWeightBold">{property.name}</Box>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                {property.address && (<Box mt={0.5} display="flex" alignItems="center">
                    <Typography variant="caption">
                      {property.address.street1}, {property.address.district.name}, {property.address.province.name},{" "}
                      {property.address.zipCode}
                    </Typography>
                  </Box>)}
              </Grid>
              {property.description ? (<Grid item xs={12} md={12} lg={12}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {property.description}
                    </Typography>
                  </Box>
                </Grid>) : null}
              <Grid item xs={12} md={12} lg={12}>
                {property.services.length > 0 && (<Box display="flex" mt={1} gridGap="10px" alignItems="center">
                    <Box flex="0 0 24px" width={24}>
                      <PlusCircle fontSize="small"/>
                    </Box>
                    <Box display="flex" style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "2",
                    WebkitBoxOrient: "vertical"
                }} flexWrap="wrap" alignItems="center">
                      {property.services.map((services, index) => {
                    return (<Typography key={index} style={{ padding: "0 2px" }} variant="caption">
                            {services.serviceName},
                          </Typography>);
                })}
                    </Box>
                  </Box>)}
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                {property.facility?.length > 0 && (<Box display="flex" gridGap="10px" alignItems="center">
                    <Box flex="0 0 24px" width={24}>
                      <Home fontSize="small"/>
                    </Box>
                    <Box display="flex" style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "2",
                    WebkitBoxOrient: "vertical"
                }} flexWrap="wrap" alignItems="center">
                      {property.facility.map((facilities, index) => {
                    if (index == property.facility?.length - 1) {
                        return (<Typography key={index} variant="caption">
                              {facilities}
                            </Typography>);
                    }
                    else {
                        return (<Typography key={index} variant="caption" style={{ marginRight: "3px" }}>
                              {facilities},
                            </Typography>);
                    }
                })}
                    </Box>
                  </Box>)}
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                {property.phone && (<Box display="flex" alignItems="center">
                    <Phone fontSize="small"/>
                    <Typography style={{ padding: "0 10px" }} variant="caption">
                      +{property.phone}
                    </Typography>
                  </Box>)}
              </Grid>
              {store.user.plan !== "starter" && (<Grid item xs={12} md={12} lg={12}>
                  {property.rooms ? (<Box display="flex" alignItems="center">
                      <BedSingle fontSize="small"/>
                      <Typography style={{ padding: "0 10px" }} variant="caption">
                        {property.rooms.length} {t("Room")}
                      </Typography>
                    </Box>) : (<Box display="flex" alignItems="center">
                      <BedSingle fontSize="small"/>
                      <Typography style={{ padding: "0 10px" }} variant="caption">
                        0 {t("Room")}
                      </Typography>
                    </Box>)}
                </Grid>)}
            </Grid>
            <PermissionGuard permission="property.edit">
              <Box mt={1} display="flex">
                <Box mr={1}>
                  <Chip size="small" icon={<Pencil className={classes.ChipEdit}/>} label={t("Edit")} className={`${classes.ChipEdit} shepherd-propertyList-editPropertyButton-${cardIndex}`} onClick={onClick}/>
                </Box>
                <Box>
                  {store.room?.pagination?.total < limitRoom && store.user.plan !== "starter" ? (<Chip size="small" icon={<BedSingle className={classes.ChipAdd}/>} label={t("Add Room")} className={`${classes.ChipAdd} shepherd-propertyList-addRoomsButton-${cardIndex}`} onClick={handleCreateRoom}/>) : store.user.plan === "starter" ? null : (<Chip size="small" icon={<BedSingle className={classes.ChipAddDisable}/>} label={t("Add Room")} className={classes.ChipAddDisable}/>)}
                </Box>
              </Box>
            </PermissionGuard>
            {store.user.isAdministrator && (<Box mt={1} display="flex">
                <Box mr={1}>
                  <Chip size="small" label={t("View Detail")} className={classes.ChipEdit} onClick={onClick}/>
                </Box>
              </Box>)}
          </Box>}/>
      <CreateRoomDialog backPage={t("Properties")} backPath={router.asPath}/>
    </Box>);
}
const SearchBar = observer(function SearchBar() {
    const { t } = useTranslation("common");
    const store = useContext(StoreContext);
    const handleSearch = useCallback((searchText) => {
        store.property.setFilters({
            searchText
        });
    }, [store.property]);
    return (<SearchFilter searchText={store.property.filters.searchText} onSearch={handleSearch} label={t("property name")} className="shepherd-propertyList-searchBar"/>);
});
function PropertyList({ properties }) {
    const { t } = useTranslation("common");
    return properties.length ? (<List component="nav" disablePadding aria-labelledby="property-list">
      <Grid container spacing={2}>
        {properties.map((property, index) => {
            return (<Grid key={property._id} item xs={12} md={6} lg={4}>
              <PrimaryCard className={`shepherd-propertyList-propertyCard-${index}`}>
                <PropertyListItem key={property._id} property={property} cardIndex={index}/>
              </PrimaryCard>
            </Grid>);
        })}
      </Grid>
    </List>) : (<EmptyIllustration label={t("No properties found")}/>);
}
async function fetchData(store, t, router) {
    const tourSteps = propertiesSteps(store, router, t);
    const isSkipGuide = store.user.isSkipTourGuide.properties;
    const results = await Promise.all([
        store.room.fetch(),
        store.property.fetch(),
        // store.subscription.fetchData(),
        store.subscription.fetchByRealmId(store.organization.selected._id),
        store.guideTour.setupTour(tourSteps),
    ]);
    if (!isSkipGuide) {
        store.guideTour.startTour(store.user);
    }
    return results;
    // return await Promise.all([
    //   store.room.fetch(),
    //   store.property.fetch(),
    //   store.subscription.fetchData(),
    // ]);
}
const Properties = observer(function Properties() {
    const { t } = useTranslation("common");
    const router = useRouter();
    const store = useContext(StoreContext);
    const [dataPagination, setDataPagination] = useState([]);
    const [pageData, setPageData] = useState([]);
    const [processedPageData, setProcessedPageData] = useState([]);
    const [Pagination] = usePagination(20, dataPagination, setPageData);
    const [NewPropertyDialog, setOpenNewPropertyDialog] = useNewPropertyDialog();
    const limitRoom = store.subscription.selected?.usage.activeRoom ||
        store.subscription.selected?.features.maxRoom ||
        store.subscription.selected?.maxRoom;
    const onNewProperty = useCallback(() => {
        setOpenNewPropertyDialog(true);
    }, [setOpenNewPropertyDialog]);
    const onStartTour = useCallback(() => {
        const tourSteps = propertiesSteps(store, router, t);
        store.guideTour.setupTour(tourSteps);
        store.guideTour.startTour(store.user);
    }, [store, router, t]);
    // Fetch Data
    const [fetching] = useFillStore(fetchData, [t, router]);
    useEffect(() => {
        if (!fetching) {
            setDataPagination(store.property.filteredItems);
        }
    }, [fetching, store.property.filteredItems]);
    useEffect(() => {
        const processProperties = async () => {
            if (pageData.length === 0) {
                setProcessedPageData([]);
                return;
            }
            try {
                const processed = await Promise.all(pageData.map(async (property) => {
                    const logo = property?.templateSetting?.logo?.selected;
                    if (logo) {
                        const logoUrl = await getPresignedUrl({
                            documentCategory: "property_logo",
                            key: property.templateSetting.logo.url
                        });
                        property.templateSetting.logo.url = logoUrl;
                    }
                    return property;
                }));
                setProcessedPageData(processed);
            }
            catch (error) {
                console.error("Error processing properties:", error);
                setProcessedPageData(pageData);
            }
        };
        processProperties();
    }, [pageData]);
    return (<PermissionGate permission="property">
      <FeatureGuard featureName="Property Management" minPlan="starter">
        <Page maxWidth="xl" loading={fetching} ActionBar={<Box display="flex" justifyContent="end">
              <Hidden smDown>
                <ButtonGroup>
                  {/* Tour Button */}
                  {store.guideTour.canShowGuide(store.user) && (<Tooltip title={t("Page Guide")} aria-label="properties-guide">
                      <Button variant="contained" style={{ marginRight: 12, padding: 0 }} onClick={onStartTour}>
                        <HelpOutlineIcon />
                      </Button>
                    </Tooltip>)}
                  <PermissionGuard permission="property.add">
                    <Button startIcon={<AddIcon />} variant="contained" disabled={store.room?.pagination?.total >= limitRoom || store.user.role == "administrator"} onClick={onNewProperty} style={{ backgroundColor: "#FFD140" }} className="shepherd-propertyList-addPropertyButton">
                      {t("Add a property")}
                    </Button>
                  </PermissionGuard>
                </ButtonGroup>
              </Hidden>
              <Hidden mdUp>
                <ButtonGroup variant="text">
                  {store.guideTour.canShowGuide(store.user) && (<Tooltip title={t("Page Guide")} aria-label="properties-guide">
                      <Button onClick={onStartTour}>
                        <HelpOutlineIcon />
                      </Button>
                    </Tooltip>)}
                  <PermissionGuard permission="property.add">
                    <MobileButton label={t("Add a property")} Icon={AddIcon} onClick={onNewProperty} disabled={store.room?.pagination?.total >= limitRoom || store.user.role == "administrator"} 
        // style={{ backgroundColor: "#FFD140" }}
        className="shepherd-propertyList-addPropertyButton"/>
                  </PermissionGuard>
                </ButtonGroup>
              </Hidden>
            </Box>}>
          <Hidden smDown>
            <Box display="flex" alignItems="end" justifyContent="space-between">
              <SearchBar />
              <Pagination />
            </Box>
          </Hidden>
          <Hidden mdUp>
            <SearchBar />
            <Box display="flex" justifyContent="center">
              <Pagination />
            </Box>
          </Hidden>
          <PropertyList properties={processedPageData}/>
          <Box display="flex" justifyContent="center">
            <Pagination />
          </Box>
          <NewPropertyDialog backPage={t("Properties")} backPath={router.asPath}/>
        </Page>
      </FeatureGuard>
    </PermissionGate>);
});
const __Next_Translate__Page__1a15d0e8340__ = withAuthentication(Properties);

    export default __appWithI18n(__Next_Translate__Page__1a15d0e8340__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
    });
  